import React, { useState, useEffect, useRef, useMemo } from "react";

/* ═══════════════════════════════════════════════════════════════════════
   EVALORE — THE WEIGHING CANVAS
   "The Weighing Moment": candidates → criterion stream → composite fit
   Pure SVG + CSS keyframes + React state (no Framer Motion, no WebGL)
   ═══════════════════════════════════════════════════════════════════════ */

/* ───────── VENDOR DEFINITIONS ───────── */
const VENDORS_INIT = [
  { id: "V1", label: "CANDIDATE V1", mark: "circle",  color: "#2E70AD", score: 78, meta: { fn: 82, tech: 71, impl: 64, tco: 78, vend: 89, sec: 73 } },
  { id: "V2", label: "CANDIDATE V2", mark: "triangle",color: "#1E4E8C", score: 71, meta: { fn: 74, tech: 68, impl: 70, tco: 66, vend: 72, sec: 75 } },
  { id: "V3", label: "CANDIDATE V3", mark: "square",  color: "#5790C9", score: 87, meta: { fn: 91, tech: 83, impl: 78, tco: 85, vend: 92, sec: 88 } },
  { id: "V4", label: "CANDIDATE V4", mark: "hexagon", color: "#143A6B", score: 64, meta: { fn: 66, tech: 62, impl: 58, tco: 72, vend: 61, sec: 68 } },
  { id: "V5", label: "CANDIDATE V5", mark: "diamond", color: "#2E70AD", score: 69, meta: { fn: 70, tech: 72, impl: 61, tco: 64, vend: 70, sec: 72 } },
  { id: "V6", label: "CANDIDATE V6", mark: "parallel",color: "#5790C9", score: 59, meta: { fn: 58, tech: 60, impl: 55, tco: 62, vend: 63, sec: 56 } },
];

/* ───────── CRITERIA POOL ───────── */
const CRITERIA_POOL = [
  { code: "CPCT-024", cat: "FUNCTIONAL FIT",      icon: "circle",   mkey: "fn",   weight: 3 },
  { code: "CPCT-047", cat: "FUNCTIONAL FIT",      icon: "circle",   mkey: "fn",   weight: 3 },
  { code: "CPCT-093", cat: "FUNCTIONAL FIT",      icon: "circle",   mkey: "fn",   weight: 2 },
  { code: "CPCT-112", cat: "FUNCTIONAL FIT",      icon: "circle",   mkey: "fn",   weight: 3 },
  { code: "CPCT-156", cat: "FUNCTIONAL FIT",      icon: "circle",   mkey: "fn",   weight: 2 },
  { code: "CPCT-189", cat: "FUNCTIONAL FIT",      icon: "circle",   mkey: "fn",   weight: 3 },
  { code: "TECH-012", cat: "TECHNICAL ARCH",       icon: "square",   mkey: "tech", weight: 2 },
  { code: "TECH-034", cat: "TECHNICAL ARCH",       icon: "square",   mkey: "tech", weight: 3 },
  { code: "TECH-071", cat: "TECHNICAL ARCH",       icon: "square",   mkey: "tech", weight: 2 },
  { code: "TECH-098", cat: "TECHNICAL ARCH",       icon: "square",   mkey: "tech", weight: 3 },
  { code: "TECH-124", cat: "TECHNICAL ARCH",       icon: "square",   mkey: "tech", weight: 1 },
  { code: "IMPL-007", cat: "IMPLEMENTATION",      icon: "triangle", mkey: "impl", weight: 2 },
  { code: "IMPL-023", cat: "IMPLEMENTATION",      icon: "triangle", mkey: "impl", weight: 2 },
  { code: "IMPL-056", cat: "IMPLEMENTATION",      icon: "triangle", mkey: "impl", weight: 3 },
  { code: "IMPL-089", cat: "IMPLEMENTATION",      icon: "triangle", mkey: "impl", weight: 1 },
  { code: "TCO-015",  cat: "5-YEAR TCO",           icon: "money",    mkey: "tco",  weight: 3 },
  { code: "TCO-041",  cat: "5-YEAR TCO",           icon: "money",    mkey: "tco",  weight: 2 },
  { code: "TCO-067",  cat: "5-YEAR TCO",           icon: "money",    mkey: "tco",  weight: 2 },
  { code: "TCO-098",  cat: "5-YEAR TCO",           icon: "money",    mkey: "tco",  weight: 3 },
  { code: "VEND-018", cat: "VENDOR VIABILITY",    icon: "diamond",  mkey: "vend", weight: 2 },
  { code: "VEND-042", cat: "VENDOR VIABILITY",    icon: "diamond",  mkey: "vend", weight: 2 },
  { code: "VEND-076", cat: "VENDOR VIABILITY",    icon: "diamond",  mkey: "vend", weight: 1 },
  { code: "SEC-011",  cat: "SECURITY",             icon: "shield",   mkey: "sec",  weight: 2 },
  { code: "SEC-029",  cat: "SECURITY",             icon: "shield",   mkey: "sec",  weight: 3 },
  { code: "SEC-054",  cat: "SECURITY",             icon: "shield",   mkey: "sec",  weight: 2 },
  { code: "SEC-087",  cat: "SECURITY",             icon: "shield",   mkey: "sec",  weight: 3 },
];

const META_ORDER = [
  { key: "fn",   label: "FUNCTIONAL FIT" },
  { key: "tech", label: "TECHNICAL ARCH" },
  { key: "impl", label: "IMPLEMENTATION" },
  { key: "tco",  label: "5-YEAR TCO" },
  { key: "vend", label: "VENDOR VIABILITY" },
  { key: "sec",  label: "SECURITY" },
];

/* ───────── ZONE LAYOUT (viewBox 640 × 540) ───────── */
const Z = {
  topBar: 26,
  zoneTop: 50,
  zoneBottom: 510,
  leftEnd: 190,
  centerStart: 195,
  centerEnd: 450,
  rightStart: 455,
};

const clampScore = (s) => Math.max(40, Math.min(99, s));
const uuid = () => Math.random().toString(36).slice(2, 10);

/* ═══════════════════════════════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════════════════════════════ */
export default function EvaloreWeighingCanvas() {
  const [vendors, setVendors] = useState(VENDORS_INIT);
  const [criteria, setCriteria] = useState([]);
  const [beams, setBeams] = useState([]);
  const [progress, setProgress] = useState(142);
  const [leader, setLeader] = useState("V3");
  const [leaderSpark, setLeaderSpark] = useState(null);
  const [reduceMotion, setReduceMotion] = useState(false);
  const critIdRef = useRef(0);
  const lastLeaderRef = useRef("V3");

  /* reduced-motion */
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduceMotion(mq.matches);
    const fn = (e) => setReduceMotion(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);

  /* rank by score; rank 1 = highest score */
  const ranked = useMemo(() => [...vendors].sort((a, b) => b.score - a.score), [vendors]);
  const rankOf = useMemo(() => Object.fromEntries(ranked.map((v, i) => [v.id, i + 1])), [ranked]);

  /* detect leader change → orange spark */
  useEffect(() => {
    const newLead = ranked[0].id;
    if (newLead !== lastLeaderRef.current) {
      const id = uuid();
      setLeaderSpark({ id, vendor: newLead });
      setTimeout(() => setLeaderSpark((s) => (s && s.id === id ? null : s)), 400);
      lastLeaderRef.current = newLead;
      setLeader(newLead);
    }
  }, [ranked]);

  /* criterion spawner — every 1.8s spawn, 8s travel */
  useEffect(() => {
    if (reduceMotion) return;
    let alive = true;
    const spawn = () => {
      if (!alive) return;
      const base = CRITERIA_POOL[Math.floor(Math.random() * CRITERIA_POOL.length)];
      // compute scores per vendor (1-5 filled dots)
      const scores = vendors.map((v) => {
        const mscore = v.meta[base.mkey] || 60;
        return Math.max(1, Math.min(5, Math.round((mscore - 50) / 10)));
      });
      // add realistic jitter
      const scoresJ = scores.map((s) => Math.max(1, Math.min(5, s + (Math.random() < 0.35 ? (Math.random() < 0.5 ? -1 : 1) : 0))));
      const isCritical = Math.random() < 0.12;
      const id = ++critIdRef.current;
      setCriteria((prev) => [
        ...prev.slice(-7),
        { id, ...base, scores: scoresJ, critical: isCritical, born: Date.now(), fired: false },
      ]);
      // remove after full traverse (8.5s)
      setTimeout(() => setCriteria((p) => p.filter((c) => c.id !== id)), 8500);
      setTimeout(spawn, 1800 + Math.random() * 400);
    };
    // initial stagger
    for (let i = 0; i < 3; i++) setTimeout(spawn, i * 1400);
    return () => { alive = false; };
  }, [reduceMotion, vendors]);

  /* impact firing — check which pills are near the midpoint, fire beams, update vendor scores */
  useEffect(() => {
    if (reduceMotion) return;
    const itv = setInterval(() => {
      const now = Date.now();
      setCriteria((prev) => {
        let toFire = null;
        const updated = prev.map((c) => {
          const elapsed = (now - c.born) / 1000;
          const progressPct = elapsed / 8; // 0..1
          // fire near midpoint (45-55% of travel)
          if (!c.fired && progressPct > 0.46 && progressPct < 0.58) {
            toFire = c;
            return { ...c, fired: true };
          }
          return c;
        });
        if (toFire) {
          // create beams (one per vendor row)
          const beamBatchId = uuid();
          const newBeams = VENDORS_INIT.map((v, i) => ({
            id: uuid(),
            batchId: beamBatchId,
            pillId: toFire.id,
            vendorId: v.id,
            score: toFire.scores[i],
            weight: toFire.weight,
            critical: toFire.critical,
            born: now,
          }));
          setBeams((bs) => [...bs.slice(-24), ...newBeams]);
          setTimeout(() => setBeams((bs) => bs.filter((b) => b.batchId !== beamBatchId)), 500);
          // update vendor scores
          setVendors((cur) =>
            cur.map((v, i) => {
              const sc = toFire.scores[i]; // 1-5
              const delta = (sc - 3) * (0.25 + toFire.weight * 0.2) * (toFire.critical ? 1.5 : 1);
              return { ...v, score: clampScore(v.score + delta) };
            })
          );
          // bump progress
          setProgress((p) => (p >= 240 ? 60 + Math.floor(Math.random() * 20) : p + 1));
        }
        return updated;
      });
    }, 200);
    return () => clearInterval(itv);
  }, [reduceMotion]);

  /* ─────────── DERIVED COMPOSITE DIAL ─────────── */
  const leaderVendor = ranked[0];
  const compositeFit = Math.round(leaderVendor.score);
  const dialCirc = 2 * Math.PI * 58;
  const dialFill = (compositeFit / 100) * dialCirc;

  /* ─────────── RENDER ─────────── */
  return (
    <div className="nw-wrap" data-testid="nw-canvas" role="img"
         aria-label="Animated illustration: Evalore evaluates six candidate platforms against weighted criteria. Six vendors are ranked live on the left. In the center, criterion pills flow downward and fire scoring beams to each vendor. On the right, a composite fit-score dial and meta-category breakdown bars update in real time.">
      <svg viewBox="0 0 640 540" className={`nw-svg ${reduceMotion ? "nw-reduced" : ""}`}
           preserveAspectRatio="xMidYMid meet" aria-hidden="true">
        <defs>
          <linearGradient id="nw-center-grad" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%"  stopColor="#1E4E8C" stopOpacity="0" />
            <stop offset="10%" stopColor="#1E4E8C" stopOpacity="0.05" />
            <stop offset="50%" stopColor="#1E4E8C" stopOpacity="0.11" />
            <stop offset="90%" stopColor="#1E4E8C" stopOpacity="0.05" />
            <stop offset="100%" stopColor="#1E4E8C" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* ── TOP OVERLAY STRIP ── */}
        <g className="nw-topbar">
          <rect x="0" y="0" width="640" height={Z.topBar} className="nw-topbar-bg" />
          <text x="8" y="17" className="nw-topbar-text">EVALUATION · SCOPE</text>
          <text x="340" y="17" textAnchor="middle" className="nw-topbar-text-mid">
            FN 30 · TECH 20 · IMPL 15 · TCO 15 · VEND 10 · SEC 10
          </text>
          <g transform="translate(548,12)">
            <path d="M0,-5 L-3,0 L0,0 L-1,4 L4,-2 L1,-2 Z" className="nw-spark" />
            <text x="8" y="4" className="nw-topbar-text">AI-AUGMENTED</text>
          </g>
          <line x1="0" y1={Z.topBar + 1} x2="640" y2={Z.topBar + 1} className="nw-hair" />
        </g>

        {/* ── ZONE HEADERS ── */}
        <text x="8"    y={Z.zoneTop - 8} className="nw-zone-header">6 CANDIDATES · LIVE RANKING</text>
        <text x="315"  y={Z.zoneTop - 8} textAnchor="middle" className="nw-zone-header">240 CRITERIA · 6 META-CATEGORIES</text>
        <text x={Z.rightStart + 8} y={Z.zoneTop - 8} className="nw-zone-header">COMPOSITE FIT SCORE · LIVE</text>

        {/* ── CENTER ZONE: stream backdrop ── */}
        <rect x={Z.centerStart} y={Z.zoneTop} width={Z.centerEnd - Z.centerStart} height={Z.zoneBottom - Z.zoneTop}
              fill="url(#nw-center-grad)" className="nw-center-bg" />
        {/* left & right hairlines of center stream */}
        <line x1={Z.centerStart} y1={Z.zoneTop} x2={Z.centerStart} y2={Z.zoneBottom} className="nw-zone-edge" />
        <line x1={Z.centerEnd}   y1={Z.zoneTop} x2={Z.centerEnd}   y2={Z.zoneBottom} className="nw-zone-edge" />
        {/* impact midpoint marker */}
        <line x1={Z.centerStart} y1={(Z.zoneTop + Z.zoneBottom) / 2}
              x2={Z.centerEnd}   y2={(Z.zoneTop + Z.zoneBottom) / 2}
              className="nw-impact-line" />

        {/* ── LEFT ZONE: VENDOR ROWS (rendered in RANK ORDER) ── */}
        <g className="nw-vendors">
          {ranked.map((v, idx) => (
            <VendorRow
              key={v.id}
              vendor={v}
              rank={idx + 1}
              isLeader={idx === 0}
              sparkOn={leaderSpark && leaderSpark.vendor === v.id}
              beamActive={beams.some((b) => b.vendorId === v.id)}
            />
          ))}
        </g>

        {/* ── CENTER ZONE: CRITERION PILLS ── */}
        <g className="nw-criteria">
          {criteria.map((c) => (
            <CriterionPill key={c.id} crit={c} />
          ))}
        </g>

        {/* ── IMPACT BEAMS (lens-style fires from pill midpoint to vendor rows) ── */}
        <g className="nw-beams">
          {beams.map((b) => {
            const rank = rankOf[b.vendorId];
            const rowY = Z.zoneTop + 14 + (rank - 1) * 66 + 24;
            const pill = criteria.find((c) => c.id === b.pillId);
            const pillY = pill ? criterionY(pill) : (Z.zoneTop + Z.zoneBottom) / 2;
            return (
              <Beam
                key={b.id}
                fromX={Z.centerStart + 60}
                fromY={pillY}
                toX={Z.leftEnd - 6}
                toY={rowY}
                score={b.score}
                weight={b.weight}
                critical={b.critical}
              />
            );
          })}
        </g>

        {/* ── RIGHT ZONE: COMPOSITE DIAL ── */}
        <g className="nw-dial" transform={`translate(${Z.rightStart + 95}, ${Z.zoneTop + 95})`}>
          <circle r="64" className="nw-dial-track" />
          <circle r="58" className="nw-dial-ring-bg" />
          <g transform="rotate(-90)">
            <circle r="58" className="nw-dial-fill"
                    strokeDasharray={`${dialFill} ${dialCirc}`} />
          </g>
          <text y="8" textAnchor="middle" className="nw-dial-num">{compositeFit}</text>
          <text y="28" textAnchor="middle" className="nw-dial-label">COMPOSITE FIT</text>
          <text y="44" textAnchor="middle" className="nw-dial-rank">RANK 1 · {leaderVendor.id}</text>
        </g>

        {/* ── RIGHT ZONE: META-CATEGORY BREAKDOWN ── */}
        <g className="nw-meta" transform={`translate(${Z.rightStart + 8}, ${Z.zoneTop + 180})`}>
          {META_ORDER.map((m, i) => {
            const val = leaderVendor.meta[m.key];
            return (
              <g key={m.key} transform={`translate(0, ${i * 28})`}>
                <text x="0" y="8" className="nw-meta-label">{m.label}</text>
                <text x="170" y="8" textAnchor="end" className="nw-meta-val">{val}</text>
                <rect x="0" y="13" width="170" height="5" rx="2.5" className="nw-meta-track" />
                <rect x="0" y="13" width={1.7 * val} height="5" rx="2.5" className="nw-meta-fill"
                      style={{ animationDelay: `${i * 0.3}s` }} />
              </g>
            );
          })}
        </g>

        {/* ── RIGHT ZONE: PROGRESS BAR ── */}
        <g transform={`translate(${Z.rightStart + 8}, ${Z.zoneBottom - 42})`}>
          <text x="0" y="0" className="nw-progress-label">EVALUATION PROGRESS</text>
          <rect x="0" y="7" width="170" height="6" rx="3" className="nw-meta-track" />
          <rect x="0" y="7" width={(progress / 240) * 170} height="6" rx="3" className="nw-progress-fill" />
          {!reduceMotion && <rect x="0" y="7" width="22" height="6" rx="3" className="nw-progress-scanner" />}
          <text x="0" y="26" className="nw-progress-count">
            <tspan>{progress}</tspan>/240 · CONF. 97.2%
          </text>
        </g>

        {/* ── BOTTOM LABELS ── */}
        <g className="nw-bottom">
          <line x1="0" y1={Z.zoneBottom + 14} x2="640" y2={Z.zoneBottom + 14} className="nw-hair" />
          <text x="8"  y={Z.zoneBottom + 28} className="nw-bottom-label">CANDIDATES</text>
          <text x="315" y={Z.zoneBottom + 28} textAnchor="middle" className="nw-bottom-label nw-bottom-label-accent">WEIGH</text>
          <text x="632" y={Z.zoneBottom + 28} textAnchor="end" className="nw-bottom-label nw-bottom-label-accent">RANK</text>
        </g>
      </svg>

      <span className="sr-only">
        Illustration: six candidate platforms are ranked live on the left as criteria flow through a
        central evaluation stream and fire scoring impacts to each vendor. A composite fit-score
        dial on the right updates in real time with a meta-category breakdown for the rank-one vendor.
      </span>
    </div>
  );
}

/* ─────────── Helper: compute criterion pill Y from elapsed time ─────────── */
function criterionY(c) {
  const elapsed = (Date.now() - c.born) / 1000;
  const p = Math.max(0, Math.min(1, elapsed / 8));
  // pill flows from y=60 (first vendor card level) to y=480 (near bottom)
  // return midpoint of pill (pill height is 48, so +24 for center)
  return 60 + p * 420 + 24;
}

/* ═══════════════════════════════════════════════════════════════════════
   VENDOR ROW COMPONENT
   ═══════════════════════════════════════════════════════════════════════ */
function VendorRow({ vendor, rank, isLeader, sparkOn, beamActive }) {
  const yTop = Z.zoneTop + 14 + (rank - 1) * 66;
  const yMid = yTop + 24;
  return (
    <g className={`nw-vrow ${isLeader ? "nw-vrow-leader" : ""} ${beamActive ? "nw-vrow-beam" : ""}`}
       style={{ transform: `translate(0px, 0px)` }}>
      {/* leader left accent */}
      {isLeader && (
        <rect x="2" y={yTop - 2} width="2" height="52" className="nw-vrow-accent" />
      )}
      {/* background row */}
      <rect x="6" y={yTop} width={Z.leftEnd - 12} height="48" rx="6" className="nw-vrow-bg" />
      {/* rank badge */}
      <circle cx="22" cy={yMid} r="10" className="nw-rank-badge" />
      <text x="22" y={yMid + 3} textAnchor="middle" className="nw-rank-num">{rank}</text>
      {/* vendor mark */}
      <VendorMark mark={vendor.mark} color={vendor.color} cx={46} cy={yMid} />
      {/* label */}
      <text x="62" y={yMid - 3} className="nw-vendor-label">{vendor.id}</text>
      <text x="62" y={yMid + 8} className="nw-vendor-sub">{vendor.label}</text>
      {/* fit bar */}
      <rect x="62" y={yMid + 12} width="80" height="4" rx="2" className="nw-fit-track" />
      <rect x="62" y={yMid + 12} width={0.8 * vendor.score} height="4" rx="2"
            className={`nw-fit-fill ${isLeader ? "nw-fit-fill-leader" : ""}`} />
      {/* orange spark for leader */}
      {isLeader && (
        <g transform={`translate(${Z.leftEnd - 14}, ${yMid - 2})`}>
          <path d="M0,-5 L-3,0 L0,0 L-1,5 L4,-1 L1,-1 Z" className="nw-spark" />
        </g>
      )}
      {/* new-leader orange flash */}
      {sparkOn && (
        <circle cx={Z.leftEnd - 14} cy={yMid + 1} r="8" className="nw-new-leader-spark" />
      )}
    </g>
  );
}

/* ─────────── VENDOR MARK (6 geometric variants) ─────────── */
function VendorMark({ mark, color, cx, cy }) {
  const s = 5; // size unit
  const props = { fill: color, className: "nw-vmark" };
  switch (mark) {
    case "circle":   return <circle cx={cx} cy={cy} r={s + 1} {...props} />;
    case "triangle": return <polygon points={`${cx - s},${cy - s} ${cx + s + 1},${cy} ${cx - s},${cy + s}`} {...props} />;
    case "square":   return <rect x={cx - s} y={cy - s} width={s * 2} height={s * 2} rx="1" {...props} />;
    case "hexagon":  {
      const r = s + 1;
      const pts = [0, 60, 120, 180, 240, 300].map((a) => {
        const rad = (a * Math.PI) / 180;
        return `${cx + r * Math.cos(rad)},${cy + r * Math.sin(rad)}`;
      }).join(" ");
      return <polygon points={pts} {...props} />;
    }
    case "diamond":  return <polygon points={`${cx},${cy - s - 1} ${cx + s + 1},${cy} ${cx},${cy + s + 1} ${cx - s - 1},${cy}`} {...props} />;
    case "parallel": return <polygon points={`${cx - s + 2},${cy - s} ${cx + s + 2},${cy - s} ${cx + s - 2},${cy + s} ${cx - s - 2},${cy + s}`} {...props} />;
    default:         return <circle cx={cx} cy={cy} r={s} {...props} />;
  }
}

/* ═══════════════════════════════════════════════════════════════════════
   CRITERION PILL COMPONENT
   ═══════════════════════════════════════════════════════════════════════ */
function CriterionPill({ crit }) {
  const style = {
    animationDuration: "8s",
  };
  const color = categoryColor(crit.cat);
  return (
    <g className={`nw-pill nw-pill-${crit.fired ? "fired" : ""} ${crit.critical ? "nw-pill-critical" : ""}`}
       style={style}>
      <g transform={`translate(${Z.centerStart + 10}, 0)`}>
        {/* pill bg */}
        <rect x="0" y="0" width="236" height="48" rx="6" className="nw-pill-bg" />
        {/* top strip */}
        <line x1="0" y1="22" x2="236" y2="22" className="nw-pill-div" />
        {/* category icon */}
        <g transform="translate(10, 11)">
          <CatIcon icon={crit.icon} />
        </g>
        {/* criterion code + category */}
        <text x="22" y="15" className="nw-pill-code">{crit.code}</text>
        <text x="76" y="15" className="nw-pill-cat">· {crit.cat}</text>
        {/* weight dots */}
        <g transform="translate(175, 10)">
          <text x="0" y="5" className="nw-pill-weight-label">W</text>
          <circle cx="14" cy="3" r="2" className={`nw-weight-dot ${crit.weight >= 1 ? "nw-weight-on" : ""}`} />
          <circle cx="22" cy="3" r="2" className={`nw-weight-dot ${crit.weight >= 2 ? "nw-weight-on" : ""}`} />
          <circle cx="30" cy="3" r="2" className={`nw-weight-dot ${crit.weight >= 3 ? "nw-weight-on" : ""}`} />
          {crit.critical && <circle cx="38" cy="3" r="2.2" className="nw-weight-critical" />}
        </g>
        {/* scoring row — 6 vendors */}
        {VENDORS_INIT.map((v, i) => (
          <g key={v.id} transform={`translate(${4 + i * 39}, 28)`}>
            <text x="0" y="9" className="nw-pill-vlabel">{v.id}</text>
            {[0,1,2,3,4].map((d) => (
              <circle key={d} cx={16 + d * 4.5} cy="7" r="1.6"
                      className={d < crit.scores[i] ? "nw-score-on" : "nw-score-off"} />
            ))}
          </g>
        ))}
      </g>
    </g>
  );
}

function categoryColor(cat) {
  return "#5790C9";
}

function CatIcon({ icon }) {
  const c = "#5790C9";
  switch (icon) {
    case "circle":   return <circle cx="4" cy="4" r="3.5" fill={c} />;
    case "square":   return <rect x="0.5" y="0.5" width="7" height="7" fill={c} />;
    case "triangle": return <polygon points="0,7 4,0 8,7" fill={c} />;
    case "money":    return <text x="0" y="8" fontFamily="JetBrains Mono,monospace" fontSize="9" fill={c} fontWeight="700">₹</text>;
    case "diamond":  return <polygon points="4,0 8,4 4,8 0,4" fill={c} />;
    case "shield":   return <path d="M4,0 L8,2 V5 Q8,8 4,8.5 Q0,8 0,5 V2 Z" fill={c} />;
    default:         return null;
  }
}

/* ═══════════════════════════════════════════════════════════════════════
   IMPACT BEAM COMPONENT
   ═══════════════════════════════════════════════════════════════════════ */
function Beam({ fromX, fromY, toX, toY, score, weight, critical }) {
  const intensity = critical ? 1.2 : weight === 3 ? 1 : weight === 2 ? 0.75 : 0.5;
  const scoreClass = score >= 4 ? "nw-beam-hi" : score >= 3 ? "nw-beam-mid" : "nw-beam-lo";
  return (
    <line x1={fromX} y1={fromY} x2={toX} y2={toY}
          className={`nw-beam ${scoreClass}`}
          style={{ opacity: intensity }} />
  );
}
